import React, { createContext, useContext, useState } from 'react';
import ApiService from '../services/api';

const JobContext = createContext();

export const useJob = () => {
  const context = useContext(JobContext);
  if (!context) {
    throw new Error('useJob must be used within a JobProvider');
  }
  return context;
};

export const JobProvider = ({ children }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({});

  // Fetch jobs with filters
  const fetchJobs = async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const queryParams = { ...filters, ...params };
      const response = await ApiService.get('/jobs', { params: queryParams });
      if (response.success) {
        setJobs(response.data?.data || response.data || []);
        return { success: true, data: response.data };
      }
      return { success: false, message: response.message };
    } catch (err) {
      const errorMsg = err.data?.message || err.message || 'Failed to fetch jobs';
      setError(errorMsg);
      return { success: false, message: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  // Create a new job
  const createJob = async (jobData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.post('/jobs', jobData);
      if (response.success) {
        return { success: true, data: response.data };
      }
      return { success: false, message: response.message };
    } catch (err) {
      const errorMsg = err.data?.message || err.message || 'Failed to create job';
      setError(errorMsg);
      return { success: false, message: errorMsg, errors: err.data?.errors };
    } finally {
      setLoading(false);
    }
  };

  // Update job
  const updateJob = async (jobId, jobData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.put(`/jobs/${jobId}`, jobData);
      if (response.success) {
        return { success: true, data: response.data };
      }
      return { success: false, message: response.message };
    } catch (err) {
      const errorMsg = err.data?.message || err.message || 'Failed to update job';
      setError(errorMsg);
      return { success: false, message: errorMsg, errors: err.data?.errors };
    } finally {
      setLoading(false);
    }
  };

  // Delete job
  const deleteJob = async (jobId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.delete(`/jobs/${jobId}`);
      if (response.success) {
        return { success: true };
      }
      return { success: false, message: response.message };
    } catch (err) {
      const errorMsg = err.data?.message || err.message || 'Failed to delete job';
      setError(errorMsg);
      return { success: false, message: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  // Apply to job
  const applyToJob = async (jobId, applicationData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.post(`/jobs/${jobId}/apply`, applicationData);
      if (response.success) {
        return { success: true, data: response.data };
      }
      return { success: false, message: response.message };
    } catch (err) {
      const errorMsg = err.data?.message || err.message || 'Failed to apply for job';
      setError(errorMsg);
      return { success: false, message: errorMsg, errors: err.data?.errors };
    } finally {
      setLoading(false);
    }
  };

  const value = {
    jobs,
    loading,
    error,
    filters,
    setFilters,
    fetchJobs,
    createJob,
    updateJob,
    deleteJob,
    applyToJob,
  };

  return (
    <JobContext.Provider value={value}>
      {children}
    </JobContext.Provider>
  );
};

